import { checkFloat } from '@/commonFunctions/utils'
import {
  MAX_AGE,
  MAX_COURTS,
  MAX_PRICE_VALUE,
  MAX_RATING,
  MIN_AGE,
  MIN_COURTS,
  MIN_RATING,
  MIN_TEAMS,
  TITLE_MAX_LENGTH,
  TOTAL_TEAMS,
  TOTAL_WAITLIST,
} from '@/constants/Common'
import {
  AGE_INTEGER,
  AGE_MAX,
  AGE_MIN,
  AGE_MORE,
  BRACKET_CLUB_MEMBER_PRICE_REQUIRED,
  BRACKET_CLUB_NON_MEMBER_PRICE_REQUIRED,
  BRACKET_NAME_REQUIRED,
  BRACKET_TYPE_REQUIRED,
  COMP_END_DATETIME_REQUIRED,
  COMP_END_DATE_MORE,
  COMP_START_DATETIME_REQUIRED,
  COMP_START_DATE_MORE,
  COURTS_ERROR,
  COURTS_REQUIRED,
  EVENT_ANNOUNCEMENT_TITLE_MAX,
  EVENT_TYPE_REQUIRED,
  MAXIMUM_AGE_REQUIRED,
  MAXIMUM_RATING_REQUIRED,
  MINIMUM_AGE_REQUIRED,
  MIXED_BRACKET_ERROR,
  PLAYER_GROUP_REQUIRED,
  PRICE_DIGIT,
  PRICE_MAX,
  PRICE_MIN,
  RATING_DIGIT,
  RATING_MAX,
  RATING_MIN,
  RATING_MORE,
  REG_END_DATETIME_REQUIRED,
  REG_END_DATE_MORE,
  REG_START_DATETIME_REQUIRED,
  TEAMS_INTEGER,
  TEAMS_MAX,
  TEAMS_MIN,
  TEAMS_REQUIRED,
  TIMEZONE_ERROR,
  WAITLIST_INTEGER,
  WAITLIST_MAX,
  WAITLIST_MIN,
  WAITLIST_REQUIRED,
} from '@/constants/MessageConstants'
import * as Yup from 'yup'

const editBracketValidationSchema = Yup.object().shape({
  playerGroup: Yup.string().required(PLAYER_GROUP_REQUIRED),
  format: Yup.string()
    .required(BRACKET_TYPE_REQUIRED)
    .when('playerGroup', {
      is: 'MIXED',
      then: () => Yup.string().test('format', MIXED_BRACKET_ERROR, (value) => value !== 'SINGLES'),
    }),
  // .test('format', '', function (value) {
  //   const { path, parent, createError } = this
  //   if (value === 'SINGLES' && parent.playerGroup === 'MIXED') {
  //     return createError({ path: path, message: MIXED_BRACKET_ERROR })
  //   }
  //   return true
  // }),
  minAge: Yup.number()
    .typeError(MINIMUM_AGE_REQUIRED)
    .required(MINIMUM_AGE_REQUIRED)
    .integer(AGE_INTEGER)
    .min(MIN_AGE, AGE_MIN)
    .max(MAX_AGE, AGE_MAX),
  maxAge: Yup.number()
    .typeError(MAXIMUM_AGE_REQUIRED)
    .integer(AGE_INTEGER)
    .required(MAXIMUM_AGE_REQUIRED)
    .min(Yup.ref('minAge'), AGE_MORE)
    .max(MAX_AGE, AGE_MAX),
  minRating: Yup.number()
    .typeError(MINIMUM_AGE_REQUIRED)
    .required(MINIMUM_AGE_REQUIRED)
    .min(MIN_RATING, RATING_MIN)
    .max(MAX_RATING, RATING_MAX)
    .test('minRatingDigits', RATING_DIGIT, (number) => checkFloat(number)),
  maxRating: Yup.number()
    .typeError(MAXIMUM_RATING_REQUIRED)
    .required(MAXIMUM_RATING_REQUIRED)
    .nullable()
    .min(Yup.ref('minRating'), RATING_MORE)
    .max(MAX_RATING, RATING_MAX)
    .test('maxRatingDigits', RATING_DIGIT, (number) => checkFloat(number)),
  name: Yup.string().required(BRACKET_NAME_REQUIRED).max(TITLE_MAX_LENGTH, EVENT_ANNOUNCEMENT_TITLE_MAX),
  elimination: Yup.string().required(EVENT_TYPE_REQUIRED),
  courts: Yup.number()
    .notRequired()
    .typeError('')
    .when('elimination', {
      is: 'COMPASS',
      then: () =>
        Yup.number()
          .typeError(COURTS_REQUIRED)
          .required(COURTS_REQUIRED)
          .min(MIN_COURTS, COURTS_ERROR)
          .max(MAX_COURTS, COURTS_ERROR),
    }),
  registrationStartDateTime: Yup.date().typeError(REG_START_DATETIME_REQUIRED).required(REG_START_DATETIME_REQUIRED),
  registrationEndDateTime: Yup.date()
    .typeError(REG_END_DATETIME_REQUIRED)
    .required(REG_END_DATETIME_REQUIRED)
    .min(Yup.ref('registrationStartDateTime'), REG_END_DATE_MORE),
  competitionStartDateTime: Yup.date()
    .typeError(COMP_START_DATETIME_REQUIRED)
    .required(COMP_START_DATETIME_REQUIRED)
    .min(Yup.ref('registrationEndDateTime'), COMP_START_DATE_MORE),
  competitionEndDateTime: Yup.date()
    .typeError(COMP_END_DATETIME_REQUIRED)
    .required(COMP_END_DATETIME_REQUIRED)
    .min(Yup.ref('competitionStartDateTime'), COMP_END_DATE_MORE),
  timeZone: Yup.string().required(TIMEZONE_ERROR),
  memberPrice: Yup.number()
    .required(BRACKET_CLUB_MEMBER_PRICE_REQUIRED)
    .typeError(BRACKET_CLUB_MEMBER_PRICE_REQUIRED)
    .test('minPriceDigits', PRICE_DIGIT, (number) => checkFloat(number))
    .test('memberFeeLimit', '', function (number) {
      const { path, parent, createError } = this
      if (number < Number(parseFloat(parent.currencyMinLimit).toFixed(2)) && number !== 0) {
        return createError({
          path,
          message: `${PRICE_MIN}${parseFloat(parent.currencyMinLimit).toFixed(2)}.`,
        })
      }
      return true
    })
    .max(MAX_PRICE_VALUE, PRICE_MAX),
  nonMemberPrice: Yup.number()
    .required(BRACKET_CLUB_NON_MEMBER_PRICE_REQUIRED)
    .typeError(BRACKET_CLUB_NON_MEMBER_PRICE_REQUIRED)
    .test('maxPriceDigits', PRICE_DIGIT, (number) => checkFloat(number))
    .test('nonMemberFeeLimit', '', function (number) {
      const { path, parent, createError } = this
      if (number < Number(parseFloat(parent.currencyMinLimit).toFixed(2)) && number !== 0) {
        return createError({
          path,
          message: `${PRICE_MIN}${parseFloat(parent.currencyMinLimit).toFixed(2)}.`,
        })
      }
      return true
    })
    .max(MAX_PRICE_VALUE, PRICE_MAX),
  maxTeam: Yup.number()
    .required(TEAMS_REQUIRED)
    .typeError(TEAMS_REQUIRED)
    .integer(TEAMS_INTEGER)
    .min(MIN_TEAMS, TEAMS_MIN)
    .max(TOTAL_TEAMS, TEAMS_MAX),
  waitList: Yup.number()
    .required(WAITLIST_REQUIRED)
    .min(0, WAITLIST_MIN)
    .typeError(WAITLIST_REQUIRED)
    .integer(WAITLIST_INTEGER)
    .max(TOTAL_WAITLIST, WAITLIST_MAX),
})

export default editBracketValidationSchema
